//===----------------------------------------------------------------------===//
//
//  Copyright 2024-2026 Trovares Inc. dba Rocketgraph.  All rights reserved.
//
//===----------------------------------------------------------------------===//

import { useRef } from 'react';
import { useSnackbar, useStatusBanner } from '@/hooks/useSnackbar';
import { useAsyncJobs } from '@/hooks/useAsyncJobs';
import { useDataset } from '@/hooks/useDataset';
import { useJobsActions } from '@/hooks/useJobsActions';
import { useInvalidateDatasets } from '@/services/xgtQueries';
import { type Job, type JobProgress } from '@/services/apiTypes';
import { compactCount, phaseLabel } from '@/components/Jobs/jobProgress';
import { ACTIVE_STATUSES } from '@/components/Jobs/JobStatusMeta';
import { demoData } from './DemoDatasets';
import { getErrorMessage } from './Format';

// Frame name from a load job's description.  The server describes an ingest as
// "Ingest to <ns>__<Frame>, files: <urls>", so pull out the frame.  A demo
// dataset can load several frames in turn (fintrans loads Accounts then
// Transactions), so naming the current frame explains why the bar restarts.
// Returns '' when the description isn't an ingest, so we just omit it then.
function jobFrameName(job: Job | null): string {
  const match = (job?.description ?? '').match(/^Ingest to\s+(.+?)\s*,/i);
  const frame = match ? (match[1].split('__').pop() ?? '').trim() : '';
  return /^[A-Za-z0-9_ ]{1,32}$/.test(frame) ? frame : '';
}

// Demo load line, e.g. "Building adjacency · 1.0M/7.0M rows" or "Reading".  The
// percent is shown in the circular bar, so it isn't repeated here; a bare "1/3"
// step reads as meaningless, so it's left out too.
function demoProgressText(p: JobProgress): string {
  const head = p.substep ?
    p.substep.charAt(0).toUpperCase() + p.substep.slice(1) :
    phaseLabel(p.phase);
  const rows = p.rows_processed > 0 ?
    (p.rows_total != null ?
       `${compactCount(p.rows_processed)}/${compactCount(p.rows_total)} rows` :
       `${compactCount(p.rows_processed)} rows`) : '';
  return rows ? `${head} · ${rows}` : head;
}

export function useDemoDataLoader() {
  const showSnackbar = useSnackbar();
  const setStatus = useStatusBanner();
  const invalidateDatasets = useInvalidateDatasets();
  const { asyncStatus } = useAsyncJobs();
  const { getJobs } = useJobsActions();
  const { createDemoData, demoMenuItemDisabled, demoIsLoading,
          setDemoIsLoading } = useDataset();
  // Demo loads are serialized on the server (a creation lock), so run them one
  // at a time here too -- a second click queues behind the first instead of
  // showing a stuck "queued" banner alongside the running one.
  const queue = useRef<Promise<void>>(Promise.resolve());

  const runLoad = async (datasetName: string) => {
    const label = demoData[datasetName].label || datasetName;
    let pollId: ReturnType<typeof setInterval> | undefined;
    // Guards: don't downgrade back to "Loading…" once real progress has shown
    // (the job stops reporting progress as it finishes), and ignore any in-
    // flight poll that resolves after the load is already done.
    let sawProgress = false;
    let finished = false;
    // Track the frame job currently shown so a frame's completion is visible:
    // when the poll advances to a newer job (the next frame started), the frame
    // we were showing just finished, so flash it at 100% for a tick first.
    let shownJobId = -1;
    let shownText = '';
    try {
      // Baseline so the progress poll tracks this load's own job, not a stale
      // running one that predates it.
      let baselineId = -1;
      try {
        const latest = await getJobs({ limit: 1, newest_first: true });
        const id = Number(latest.items[0]?.id);
        // Guard a non-numeric id so baselineId + 1 can't become NaN and be
        // sent as min_job_id below.
        baselineId = Number.isFinite(id) ? id : -1;
      } catch {
        // Best effort -- without a baseline the poll just takes the newest
        // running job.
      }
      setStatus(datasetName, { text: `Loading ${label} data…`, percent: null });
      const asyncResponse = await createDemoData(datasetName);
      // Poll the running load job's live progress and surface it in the
      // persistent status banner while it loads.
      pollId = setInterval(() => {
        // The load is the last thing the demo does, so its job has the highest
        // id among jobs created after the baseline.  Pick that by max id rather
        // than trusting the server's sort order, then read its live progress.
        // Scope to the caller's own jobs: otherwise a concurrent higher-id job
        // from another user could win the max-id pick and hijack the banner.
        getJobs({ min_job_id: baselineId + 1, newest_first: true, limit: 50,
                  others_active: false })
          .then((resp) => {
            if (finished) return;
            const items = resp.items;
            if (items.length === 0) return;
            const byId = new Map(items.map(j => [Number(j.id), j]));
            const maxId = items.reduce(
              (m, j) => (Number(j.id) > m ? Number(j.id) : m),
              Number.NEGATIVE_INFINITY);
            // Stay on the frame being tracked until it actually reaches a
            // terminal status, so a later frame's job being enqueued early
            // doesn't cut it off; start on the newest frame.
            let job = shownJobId !== -1 ? byId.get(shownJobId) : undefined;
            if (!job) {
              job = byId.get(maxId);
              shownJobId = maxId;
            }
            if (!job) return;
            // Tracked frame finished and a newer frame exists: flash THIS
            // frame (its own label) at 100% so it visibly completes, then
            // advance to the newest frame next tick.  Keying off the tracked
            // frame's terminal status (not merely a higher id appearing)
            // avoids flashing 100% before it's really done, and avoids reusing
            // a stale label.
            if (!ACTIVE_STATUSES.has(job.status) && shownJobId !== maxId) {
              if (shownText)
                setStatus(datasetName, { text: shownText, percent: 100 });
              shownJobId = maxId;
              shownText = '';
              return;
            }
            const progress = job.progress ?? null;
            if (progress) {
              sawProgress = true;
              const frame = jobFrameName(job);
              // Show the frame unless it just repeats the dataset label
              // (e.g. the Netflow dataset's Netflow frame).
              const showFrame = frame !== '' &&
                frame.toLowerCase() !== label.toLowerCase();
              const text = `Loading ${label}${showFrame ? ` — ${frame}` : ''}: ` +
                    demoProgressText(progress);
              shownJobId = Number.isFinite(Number(job.id)) ?
                Number(job.id) : shownJobId;
              shownText = text;
              setStatus(datasetName, {
                text,
                percent: progress.percent ?? null
              });
            } else if (!sawProgress) {
              setStatus(datasetName,
                        { text: `Loading ${label} data…`, percent: null });
            }
          })
          .catch(() => { /* best effort; skip this tick */ });
      }, 700);
      await asyncStatus(asyncResponse);
      // Stop the poll before showing completion so a late tick (stalled at the
      // last reported percent) can't overwrite the 100%.  The server stops
      // reporting progress before the job ends, so the live poll never reaches
      // 100 on its own -- snap to it explicitly and hold it briefly so the bar
      // visibly completes instead of vanishing mid-way.
      finished = true;
      if (pollId != null) {
        clearInterval(pollId);
        pollId = undefined;
      }
      setStatus(datasetName, { text: `${label} data loaded`, percent: 100 });
      showSnackbar(`${label} data loaded.`, 'success');
      // Refetch the dataset list so the loaded demo data shows up; the derived
      // demo-menu state and the active-dataset reconcile follow from it.
      invalidateDatasets();
      // Let the completed (100%) banner register before the finally clears it.
      await new Promise((resolve) => setTimeout(resolve, 1000));
    } catch (error) {
      showSnackbar(
        `${label} data failed to load: ${getErrorMessage(error)}`,
        'error'
      );
      console.error('Failed to load data:', error);
    } finally {
      finished = true;
      if (pollId != null) clearInterval(pollId);
      setStatus(datasetName, null);
      setDemoIsLoading((prev: Record<string, boolean>) => ({ ...prev, [datasetName]: false }));
    }
  };

  const loadDemoData = (datasetName: string) => {
    // Mark pending right away so the menu item disables and can't be queued
    // twice, then chain onto the queue so it runs after any in-flight load.
    setDemoIsLoading((prev: Record<string, boolean>) =>
      ({ ...prev, [datasetName]: true }));
    queue.current = queue.current
      .catch(() => { /* a failed load shouldn't break the queue */ })
      .then(() => runLoad(datasetName));
    return queue.current;
  };

  const demoLoaded = demoMenuItemDisabled;
  return { loadDemoData, demoLoaded, demoIsLoading };
}
